import { create } from 'zustand';
import { persist } from 'zustand/middleware';

const uuid = () => crypto.randomUUID();

function emptyCart(orderType = 'takeaway') {
  return {
    clientUuid: uuid(),
    billId: null,
    orderType,
    source: 'counter',
    tableNo: '',
    guestCount: null,
    customer: null,
    lines: [],
    billDiscount: null,
    couponCode: '',
    notes: '',
    sentQty: {}, // lineId → qty already sent to the kitchen (from the server)
    sentLines: [], // those lines as loaded (names for cancellation tickets printed offline)
    tokenNo: null,
    baseUpdatedAt: null, // the server copy's version when an open order was loaded (offline conflicts)
  };
}

const sameLine = (a, b) =>
  a.itemId === b.itemId &&
  (a.variantId ?? null) === (b.variantId ?? null) &&
  [...(a.modifierOptionIds ?? [])].sort().join() === [...(b.modifierOptionIds ?? [])].sort().join() &&
  !a.note &&
  !b.note &&
  !a.discount &&
  !b.discount;

/**
 * The cart being billed on this device. Persisted to localStorage so a reload or a crash
 * never loses an order mid-way (offline work itself is kept in IndexedDB, lib/offline).
 */
export const useCart = create(
  persist(
    (set, get) => ({
      outletId: null,
      cart: emptyCart(),

      ensureOutlet: (outletId) => {
        if (get().outletId !== outletId) set({ outletId, cart: emptyCart() });
      },
      reset: (orderType) => set({ cart: emptyCart(orderType ?? get().cart.orderType) }),
      patch: (patch) => set({ cart: { ...get().cart, ...patch } }),

      addLine: (line) => {
        const { cart } = get();
        const existing = cart.lines.find((l) => sameLine(l, line));
        const lines = existing
          ? cart.lines.map((l) => (l === existing ? { ...l, qty: Math.min(999, l.qty + (line.qty ?? 1)) } : l))
          : [...cart.lines, { lineId: uuid(), qty: 1, modifierOptionIds: [], note: '', discount: null, ...line }];
        set({ cart: { ...cart, lines } });
      },
      updateLine: (lineId, patch) => {
        const { cart } = get();
        set({ cart: { ...cart, lines: cart.lines.map((l) => (l.lineId === lineId ? { ...l, ...patch } : l)) } });
      },
      setQty: (lineId, qty) => {
        const { cart } = get();
        if (qty <= 0) set({ cart: { ...cart, lines: cart.lines.filter((l) => l.lineId !== lineId) } });
        else
          set({
            cart: {
              ...cart,
              lines: cart.lines.map((l) => (l.lineId === lineId ? { ...l, qty: Math.min(999, qty) } : l)),
            },
          });
      },
      removeLine: (lineId) => get().setQty(lineId, 0),

      /** Load an open order into the cart: from the server, or the copy on this device (offline). */
      loadBill: (bill) =>
        set({
          cart: {
            clientUuid: bill.clientUuid,
            billId: bill.id,
            orderType: bill.orderType,
            source: bill.source,
            tableNo: bill.tableNo ?? '',
            guestCount: bill.guestCount ?? null,
            customer: bill.customer,
            lines: bill.lines.map((l) => ({
              lineId: l.lineId,
              itemId: l.itemId,
              variantId: l.variantId,
              modifierOptionIds: l.modifiers.map((m) => m.optionId),
              qty: l.qty,
              note: l.note ?? '',
              discount: l.discount ?? null,
            })),
            billDiscount: bill.billDiscount
              ? {
                  type: bill.billDiscount.type,
                  valueBp: bill.billDiscount.valueBp,
                  valuePaise: bill.billDiscount.valuePaise,
                  reason: bill.billDiscount.reason,
                }
              : null,
            couponCode: bill.coupon?.code ?? '',
            notes: bill.notes ?? '',
            sentQty: Object.fromEntries(bill.lines.map((l) => [l.lineId, l.kotQty ?? 0])),
            sentLines: bill.lines
              .filter((l) => (l.kotQty ?? 0) > 0)
              .map((l) => ({
                lineId: l.lineId,
                itemId: l.itemId,
                name: l.name,
                variantName: l.variantName ?? null,
                modifiers: (l.modifiers ?? []).map((m) => m.name),
                note: l.note ?? null,
                foodType: l.foodType,
              })),
            tokenNo: bill.tokenNo ?? null,
            // A server bill: its version. This device's copy: the server version it was based on.
            baseUpdatedAt: 'local' in bill ? (bill.baseUpdatedAt ?? null) : (bill.updatedAt ?? null),
          },
        }),
    }),
    { name: 'shripos.cart', version: 1 },
  ),
);

/** Request body for POST /bills from the cart. */
export function cartToBillInput(cart, action, { payments, expectedTotalPaise } = {}) {
  const discount = (d) =>
    d
      ? d.type === 'percent'
        ? { type: 'percent', valueBp: d.valueBp }
        : { type: 'flat', valuePaise: d.valuePaise }
      : null;
  return {
    clientUuid: cart.clientUuid,
    action,
    orderType: cart.orderType,
    source: cart.source,
    tableNo: cart.tableNo || null,
    guestCount: cart.guestCount || null,
    customerId: cart.customer?.id ?? null,
    lines: cart.lines.map((l) => ({
      lineId: l.lineId,
      itemId: l.itemId,
      variantId: l.variantId ?? null,
      modifierOptionIds: l.modifierOptionIds ?? [],
      qty: l.qty,
      note: l.note || null,
      discount: discount(l.discount),
    })),
    billDiscount: cart.billDiscount
      ? { ...discount(cart.billDiscount), reason: cart.billDiscount.reason || null }
      : null,
    couponCode: cart.couponCode || null,
    notes: cart.notes || null,
    ...(payments ? { payments } : {}),
    ...(expectedTotalPaise != null ? { expectedTotalPaise } : {}),
  };
}
